import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ChevronDown, ArrowRight } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Organization {
  id: string;
  name: string;
  slug: string;
}

type ViewMode = 'admin' | 'editor' | 'moderator' | 'member' | 'public';

const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  admin: 'Admin',
  editor: 'Editor',
  moderator: 'Moderator',
  member: 'Logged In User',
  public: 'Public Visitor',
};

export const SuperAdminSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const { 
    isSuperAdmin, 
    impersonatedOrgId, 
    impersonatedRole, 
    impersonatedOrgName,
    simulatedAuthState,
    setImpersonation, 
    clearImpersonation 
  } = useOrganization();
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(impersonatedOrgId || 'super_admin');
  const [selectedView, setSelectedView] = useState<ViewMode | ''>(
    impersonatedOrgId 
      ? (simulatedAuthState === 'unauthenticated' ? 'public' : (impersonatedRole as ViewMode) || 'admin')
      : ''
  );
  const [isLoading, setIsLoading] = useState(true);

  // Only render for super admins
  if (!isSuperAdmin) {
    return null;
  }

  // Fetch all organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, slug')
        .eq('is_active', true)
        .order('name');
      
      if (!error && data) {
        setOrganizations(data);
      }
      setIsLoading(false);
    };
    
    fetchOrganizations();
  }, []);

  // Update selected values when impersonation state changes externally
  useEffect(() => {
    if (impersonatedOrgId) {
      setSelectedOrgId(impersonatedOrgId);
      setSelectedView(simulatedAuthState === 'unauthenticated' ? 'public' : (impersonatedRole as ViewMode) || 'admin');
    } else {
      setSelectedOrgId('super_admin');
      setSelectedView('');
    }
  }, [impersonatedOrgId, impersonatedRole, simulatedAuthState]);

  const handleSwitch = () => {
    if (selectedOrgId === 'super_admin') {
      clearImpersonation();
      navigate('/super-admin');
      return;
    }

    if (!selectedOrgId || !selectedView) return;

    const org = organizations.find(o => o.id === selectedOrgId);
    if (!org) return;

    const isPublic = selectedView === 'public';
    const role = isPublic ? 'member' : selectedView;
    const authState = isPublic ? 'unauthenticated' : 'authenticated';

    setImpersonation(selectedOrgId, role, authState);

    // Navigate based on view mode
    if (isPublic) {
      navigate('/');
    } else if (['admin', 'editor', 'moderator'].includes(selectedView)) {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  const isImpersonating = !!impersonatedOrgId;
  const currentOrgName = impersonatedOrgName || organizations.find(o => o.id === selectedOrgId)?.name;

  return (
    <div 
      className={cn(
        "flex items-center gap-3 px-4 py-2 sticky top-0 z-50",
        isImpersonating 
          ? simulatedAuthState === 'unauthenticated'
            ? "bg-orange-500 text-orange-950"
            : "bg-amber-500 text-amber-950"
          : "bg-sky-600 text-white"
      )}
    >
      <Shield className="h-4 w-4 flex-shrink-0" />
      
      {isImpersonating ? (
        <span className="text-sm font-medium hidden sm:inline">
          Viewing: <strong>{currentOrgName}</strong> as {VIEW_MODE_LABELS[selectedView]}
        </span>
      ) : (
        <span className="text-sm font-medium hidden sm:inline">Super Admin</span>
      )}
      
      <div className="flex items-center gap-2 ml-auto">
        {/* Organization Selector */}
        <Select 
          value={selectedOrgId} 
          onValueChange={(value) => {
            setSelectedOrgId(value);
            // Clear role selection when switching to super admin dashboard
            if (value === 'super_admin') {
              setSelectedView('');
            }
          }}
          disabled={isLoading}
        >
          <SelectTrigger className={cn(
            "w-[140px] sm:w-[180px] h-8 text-xs border-0",
            isImpersonating 
              ? simulatedAuthState === 'unauthenticated'
                ? "bg-orange-600/50 text-orange-950 focus:ring-orange-700"
                : "bg-amber-600/50 text-amber-950 focus:ring-amber-700"
              : "bg-sky-700/50 text-white focus:ring-sky-400"
          )}>
            <SelectValue placeholder="Select org..." />
          </SelectTrigger>
          <SelectContent className="bg-card border-border z-[100]">
            <SelectItem value="super_admin">Super Admin Dashboard</SelectItem>
            {organizations.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* View Mode Selector */}
        <Select 
          value={selectedView} 
          onValueChange={(v) => setSelectedView(v as ViewMode)}
          disabled={selectedOrgId === 'super_admin'}
        >
          <SelectTrigger className={cn(
            "w-[120px] sm:w-[160px] h-8 text-xs border-0",
            isImpersonating 
              ? simulatedAuthState === 'unauthenticated'
                ? "bg-orange-600/50 text-orange-950 focus:ring-orange-700"
                : "bg-amber-600/50 text-amber-950 focus:ring-amber-700"
              : "bg-sky-700/50 text-white focus:ring-sky-400"
          )}>
            <SelectValue placeholder="View as..." />
          </SelectTrigger>
          <SelectContent className="bg-card border-border z-[100]">
            <SelectItem value="admin">{VIEW_MODE_LABELS.admin}</SelectItem>
            <SelectItem value="editor">{VIEW_MODE_LABELS.editor}</SelectItem>
            <SelectItem value="moderator">{VIEW_MODE_LABELS.moderator}</SelectItem>
            <SelectItem value="member">{VIEW_MODE_LABELS.member}</SelectItem>
            <SelectItem value="public">{VIEW_MODE_LABELS.public}</SelectItem>
          </SelectContent>
        </Select>

        {/* Switch Button */}
        <Button
          size="sm"
          variant="secondary"
          onClick={handleSwitch}
          disabled={selectedOrgId !== 'super_admin' && (!selectedOrgId || !selectedView)}
          className={cn(
            "h-8 px-3 text-xs font-medium",
            isImpersonating 
              ? simulatedAuthState === 'unauthenticated'
                ? "bg-orange-100 text-orange-900 hover:bg-orange-200"
                : "bg-amber-100 text-amber-900 hover:bg-amber-200"
              : "bg-white text-sky-700 hover:bg-sky-50"
          )}
        >
          <span className="hidden sm:inline">Switch</span>
          <ArrowRight className="h-3 w-3 sm:ml-1" />
        </Button>
      </div>
    </div>
  );
};
